import { useEffect } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Button, InlineAlert } from '@chillout/ui'
import { AdminScreen } from '../AdminScreen'
import { ADMIN_SECTION_ROUTE_BY_NAME } from '../adminRoutes'
import { SectionErrorState } from '../shared/SectionErrorState'
import { BirthdayRewardActiveItemSection } from './BirthdayRewardActiveItemSection'
import { BirthdayRewardEnableSection } from './BirthdayRewardEnableSection'
import { BirthdayRewardHowItWorksSection } from './BirthdayRewardHowItWorksSection'
import { BirthdayRewardItemsSection } from './BirthdayRewardItemsSection'
import { useBirthdayRewardConfig } from './useBirthdayRewardConfig'
import { useBirthdayRewardItems } from './useBirthdayRewardItems'
import { useSaveBirthdayRewardItem } from './useSaveBirthdayRewardItem'

interface BirthdayRewardScreenProps {
  onDirtyChange: (dirty: boolean) => void
}

export function BirthdayRewardScreen({ onDirtyChange }: BirthdayRewardScreenProps) {
  const { t } = useTranslation()
  const route = ADMIN_SECTION_ROUTE_BY_NAME['AdminBirthdayReward']
  const config = useBirthdayRewardConfig()
  const rewardItemsQuery = useBirthdayRewardItems()
  const rewardItems = rewardItemsQuery.data ?? []
  const saveRewardItemMutation = useSaveBirthdayRewardItem()

  useEffect(() => {
    onDirtyChange(config.hasChanges)
  }, [config.hasChanges, onDirtyChange])

  return (
    <AdminScreen
      title={t(route.titleKey)}
      subtitle={t(route.subtitleKey)}
      actions={
        <Button
          label={t('birthdayReward.saveChanges')}
          loadingLabel={t('birthdayReward.saving')}
          onPress={config.save}
          disabled={!config.hasChanges || config.missingRewardItem}
          loading={config.isSaving}
          fullWidth={false}
        />
      }
    >
      {config.isError ? (
        <SectionErrorState message={t('birthdayReward.loadError')} retryLabel={t('common.retry')} onRetry={config.onRetry} />
      ) : (
        <View className="gap-8">
          {config.saveError ? <InlineAlert message={t('birthdayReward.saveError')} /> : null}

          <BirthdayRewardEnableSection stepNumber={1} enabled={config.enabled} onEnabledChange={config.setEnabled} />

          <BirthdayRewardItemsSection
            stepNumber={2}
            rewardItems={rewardItems}
            assignedRewardItemIds={new Set(config.rewardItemId ? [config.rewardItemId] : [])}
            isLoading={rewardItemsQuery.isLoading}
            isError={rewardItemsQuery.isError}
            onRetry={() => void rewardItemsQuery.refetch()}
            onSaveItem={(input) => saveRewardItemMutation.mutateAsync(input)}
            isSaving={saveRewardItemMutation.isPending}
            isSaveError={saveRewardItemMutation.isError}
            onResetSaveState={() => saveRewardItemMutation.reset()}
            disabled={!config.enabled}
          />

          <BirthdayRewardActiveItemSection
            stepNumber={3}
            rewardItems={rewardItems}
            selectedRewardItemId={config.rewardItemId}
            onSelectedRewardItemIdChange={config.setRewardItemId}
            disabled={!config.enabled}
            error={config.missingRewardItem ? t('birthdayReward.activeItem.requiredError') : undefined}
          />

          <BirthdayRewardHowItWorksSection stepNumber={4} />
        </View>
      )}
    </AdminScreen>
  )
}
